import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { useDespia } from "@/hooks/useDespia";

const AuthCallback = () => {
  const navigate = useNavigate();
  const despia   = useDespia();

  // FIX: Mounted guard — prevents navigate/haptic calls if the component
  // unmounts while the async session exchange is in flight.
  const isMountedRef  = useRef(true);
  // FIX: Process guard — prevents double-processing caused by despia being a
  // new object reference each render, which re-triggered the effect in the original.
  const hasHandledRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  useEffect(() => {
    // FIX: Set SYNCHRONOUSLY — original set it inside async branches, which
    // allowed a second effect execution to enter before the first async resolved.
    if (hasHandledRef.current) return;
    hasHandledRef.current = true;

    const handleAuthCallback = async () => {
      try {
        console.log('=== Auth Callback Started ===');
        console.log('Current URL:', window.location.href);
        console.log('Hash:', window.location.hash);
        console.log('Search:', window.location.search);

        const hashParams   = new URLSearchParams(window.location.hash.substring(1));
        const searchParams = new URLSearchParams(window.location.search);

        const accessToken  = hashParams.get('access_token')  || searchParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token') || searchParams.get('refresh_token');
        const type         = hashParams.get('type')          || searchParams.get('type');

        console.log('Token found:', !!accessToken, 'Type:', type);

        if (accessToken && refreshToken) {
          console.log('Setting session with tokens...');

          const { data, error } = await supabase.auth.setSession({
            access_token:  accessToken,
            refresh_token: refreshToken,
          });

          if (!isMountedRef.current) return;

          if (error) {
            console.error('Error setting session:', error);
            despia.haptic.error();
            // FIX: Pass error to /auth via query param so Auth.tsx displays it.
            // Original navigated to /auth with no state — user saw no explanation.
            navigate(`/auth?error=${encodeURIComponent(error.message)}`, { replace: true });
            return;
          }

          console.log('Session set successfully:', data.user?.email);
          despia.haptic.success();

          // FIX: Password recovery links (type === 'recovery') must route to the
          // reset-password page — NOT home. The original sent every callback to
          // /home, meaning a password reset link silently logged the user in and
          // left them no way to actually change their password.
          if (type === 'recovery') {
            console.log('[AuthCallback] Password recovery — redirecting to /reset-password');
            navigate('/reset-password', { replace: true });
          } else {
            console.log('[AuthCallback] Auth success — redirecting to /home');
            navigate('/home', { replace: true });
          }
        } else {
          const errorParam = hashParams.get('error')             || searchParams.get('error');
          const errorDesc  = hashParams.get('error_description') || searchParams.get('error_description');

          if (!isMountedRef.current) return;

          if (errorParam) {
            console.error('Auth error:', errorParam, errorDesc);
            despia.haptic.error();
            // FIX: Pass decoded error to Auth page so the user knows what went wrong.
            const msg = decodeURIComponent(errorDesc || errorParam);
            navigate(`/auth?error=${encodeURIComponent(msg)}`, { replace: true });
          } else {
            console.log('No tokens found in URL, redirecting to auth');
            navigate('/auth', { replace: true });
          }
        }
      } catch (err) {
        console.error('Exception in auth callback:', err);
        if (!isMountedRef.current) return;
        despia.haptic.error();
        navigate('/auth', { replace: true });
      }
    };

    handleAuthCallback();
  // FIX: Empty deps — this handler must run exactly once on mount.
  // navigate (react-router) and despia.haptic (memoized) are stable references.
  // Including despia in deps (as original did) caused re-runs on every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="min-h-screen bg-gradient-secondary flex items-center justify-center"
      style={{
        paddingTop:    'var(--safe-area-top, 0px)',
        paddingBottom: 'var(--safe-area-bottom, 0px)',
      }}
    >
      <div className="text-center space-y-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" aria-hidden="true" />
        {/* FIX: "Verifying your email" was misleading for recovery/OAuth callbacks */}
        <p className="text-muted-foreground">Completing sign in...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
